/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useTableConfiguration from '../../../components/foTable/useTableConfiguration';
import { ConfigResourceProps } from '../../../redux/enums/ConfigResourceProps';

import { flattenObject } from '../../../shared/utils/objectUtils';
import GenericCellDataAdapter from '../../../components/foTable/adapters/GenericCellDataAdapter';
import { useConfiguration } from '../../../redux/features/configuration/useConfiguration';
import { SORT_ORDER } from '../../../components/foTable/utils/enums';
import { useInvitations } from '../../../redux/features/invitations/paginate/useInvitations';
import { getUserOrganizationId } from '../../../redux/features/organization/organization.helpers';
import { getAccessToken } from '../../../redux/features/auth/auth.service';

export const PAGE_SIZES = [
  10,
  15,
  20,
  25,
  50,
  100,
];

export default function useInvitationsTable(theming) {
  const { t } = useTranslation();

  // Fetch configurations
  const frontendCustomisationConfig = useConfiguration(ConfigResourceProps.FRONTEND_CUSTOMISATION);
  const tableConfiguration = useTableConfiguration(ConfigResourceProps.INVITATIONS_TABLE_CONFIGURATION);
  const { columns: columnsConfiguration } = tableConfiguration;

  // Table local state
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageNo, setPageNo] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState({ sortColumn: null, sortOrder: null });
  const [filter, setFilter] = useState([]);
  const [selection, setSelection] = useState([]);

  const [organizationId, setOrganizationId] = useState();
  const accessToken = getAccessToken();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const orgId = await getUserOrganizationId();
      setOrganizationId(orgId);
      setLoading(false);
    };
    if (accessToken) fetchData();
  }, [accessToken]);

  // Table global state
  const { isLoading, items, page, ready, getInvitations } = useInvitations(
    {
      numberOfElementsOnAPage: pageSize,
      pageNumber: pageNo,
      sortColumn: sort.sortColumn,
      sortOrder: sort.sortOrder,
      filter,
    },
    organizationId,
  );

  function createTableData(pagedData) {
    return pagedData?.map(r => flattenObject(r));
  }

  useEffect(() => {
    if (columnsConfiguration?.ready) {
      setColumns(
        GenericCellDataAdapter(
          columnsConfiguration?.resource,
          frontendCustomisationConfig,
          null,
          null,
          null,
          t,
          theming.theme,
          ['actions.resend', 'actions.remove'],
        ),
      );
    }
  }, [columnsConfiguration]);

  useEffect(() => {
    if (!ready) return;

    const tableData = createTableData(items);
    setRows(tableData);
    setData(items);
    setTotalCount(page.totalElements);
  }, [items]);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  // Sort functionality
  const getSortOrderForColumn = column => (sort.sortColumn === column ? sort.sortOrder : SORT_ORDER.NONE);
  const toggleSortOrder = currentSortOrder => (currentSortOrder === SORT_ORDER.ASC ? SORT_ORDER.DSC : SORT_ORDER.ASC);
  const onSortOrderClick = column => () => {
    const newSort = {
      sortColumn: column,
      sortOrder: sort.sortColumn === column ? toggleSortOrder(sort.sortOrder) : SORT_ORDER.ASC,
    };
    setSort(newSort);
  };

  // Select functionality
  const isRowSelected = row => selection.map(s => s.id).includes(row.id);
  const isAllSelected = () => rows?.length > 0 && rows.every(row => isRowSelected(row));
  const selectRow = (row) => {
    setSelection(previousSelection => [...previousSelection, row]);
  };
  const unselectRow = (row) => {
    setSelection(previousSelection => previousSelection.filter(s => s.id !== row.id));
  };
  const selectAll = () => {
    setSelection(rows);
  };
  const unselectAll = () => {
    setSelection([]);
  };

  const handleSelectRow = row => () => {
    if (isRowSelected(row)) {
      unselectRow(row);
    } else {
      selectRow(row);
    }
  };

  const handleSelectAll = () => {
    if (isAllSelected()) {
      unselectAll();
    } else {
      selectAll();
    }
  };

  return {
    tableConfiguration,
    rows,
    columns,
    data,
    totalCount,
    loading,
    pageNo,
    pageSize,
    selection,
    filter,
    organizationId,
    getInvitations,
    setPageNo,
    setPageSize,
    setFilter,
    getSortOrderForColumn,
    onSortOrderClick,
    handleSelectAll,
    handleSelectRow,
    isAllSelected,
    isRowSelected,
  };
}
